// SPDX-License-Identifier: AGPL-3.0-or-later

import { useNavigate } from 'react-router';
import { Trash2 } from 'lucide-react';
import { Button, useToast } from '@intakeform/ui';
import { useTemplates, useImportTemplate, useDeleteTemplate } from '../../api/questionnaires.js';
import type { QuestionnaireResource } from '../../api/types.js';

const JURISDICTION_EXT = 'https://intakeform.dev/fhir/StructureDefinition/jurisdiction';
const TEMPLATE_SCOPE_EXT = 'https://intakeform.dev/fhir/StructureDefinition/template-scope';

const getJurisdictionLabel = (q: QuestionnaireResource): string => {
  const ext = q.resource.extension?.find((e) => e.url === JURISDICTION_EXT);
  switch (ext?.valueString) {
    case 'US':
      return 'United States (Federal / HIPAA)';
    case 'CA-ON':
      return 'Ontario (PHIPA)';
    default:
      return 'Global / Other';
  }
};

const isOrgScopedTemplate = (q: QuestionnaireResource): boolean => {
  const ext = q.resource.extension?.find((e) => e.url === TEMPLATE_SCOPE_EXT);
  return (ext?.valueCode ?? ext?.valueString) === 'organization';
};

export function TemplatesRoute() {
  const templates = useTemplates();
  const importTemplate = useImportTemplate();
  const deleteTemplate = useDeleteTemplate();
  const navigate = useNavigate();
  const { toast } = useToast();

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Template Library</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Import and customize pre-configured clinical templates compliant with local jurisdictions.
        </p>
      </div>

      {templates.isLoading ? (
        <p className="text-muted-foreground">Loading library templates...</p>
      ) : templates.error ? (
        <div className="error-banner">Failed to load templates.</div>
      ) : (templates.data ?? []).length === 0 ? (
        <p className="text-muted-foreground">No templates available in the library.</p>
      ) : (
        (() => {
          // Org-scoped templates (promoted from a form in this org)
          // get their own bucket above the cross-org library.
          const orgScoped: QuestionnaireResource[] = [];
          const grouped: Record<string, QuestionnaireResource[]> = {};
          for (const tpl of templates.data ?? []) {
            if (isOrgScopedTemplate(tpl)) {
              orgScoped.push(tpl);
              continue;
            }
            const region = getJurisdictionLabel(tpl);
            if (!grouped[region]) grouped[region] = [];
            grouped[region].push(tpl);
          }

          const renderTemplateCard = (tpl: QuestionnaireResource, isOrg = false) => (
            <div
              key={tpl.id}
              data-testid={`template-card-${tpl.id}`}
              className="p-4 bg-surface border border-border rounded-xl flex items-center justify-between shadow-sm hover:border-primary-500 transition-all"
            >
              <div>
                <div className="font-semibold text-foreground text-base">{tpl.title}</div>
                <div className="text-xs text-muted-foreground mt-0.5 max-w-md overflow-hidden text-overflow-ellipsis whitespace-nowrap">
                  Includes:{' '}
                  {tpl.resource.item?.map((item) => item.text ?? item.linkId).join(', ') ?? 'None'}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {isOrg ? (
                  <Button
                    data-testid={`delete-template-${tpl.id}`}
                    variant="ghost"
                    disabled={deleteTemplate.isPending}
                    onClick={() => {
                      // Soft-delete via the standard questionnaires
                      // DELETE — keeps the row recoverable from Dolt.
                      if (
                        !window.confirm(`Delete "${tpl.title}"? This can't be undone from the UI.`)
                      ) {
                        return;
                      }
                      deleteTemplate.mutate(tpl.id, {
                        onSuccess: () => {
                          toast({
                            title: 'Template deleted',
                            description: `"${tpl.title}" removed from Your templates.`,
                            variant: 'success',
                          });
                        },
                        onError: (err) => {
                          toast({
                            title: 'Delete failed',
                            description: err.message,
                            variant: 'danger',
                          });
                        },
                      });
                    }}
                  >
                    <Trash2 size={14} className="mr-1.5" />
                    Delete
                  </Button>
                ) : null}
                <Button
                  variant="secondary"
                  disabled={importTemplate.isPending}
                  onClick={() => {
                    importTemplate.mutate(tpl.id, {
                      onSuccess: (res) => {
                        toast({
                          title: 'Template Imported',
                          description: `"${tpl.title}" imported successfully as draft.`,
                          variant: 'success',
                        });
                        navigate(`/builder/${res.id}`);
                      },
                      onError: (err) => {
                        toast({
                          title: 'Import failed',
                          description: err.message,
                          variant: 'danger',
                        });
                      },
                    });
                  }}
                >
                  {importTemplate.isPending ? 'Importing...' : 'Use Template'}
                </Button>
              </div>
            </div>
          );

          return (
            <div className="space-y-8">
              {orgScoped.length > 0 ? (
                <div className="space-y-3">
                  <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider border-b border-border pb-1">
                    Your templates
                  </h3>
                  <div className="grid gap-3">
                    {orgScoped.map((tpl) => renderTemplateCard(tpl, true))}
                  </div>
                </div>
              ) : null}
              {Object.entries(grouped).map(([regionName, items]) => (
                <div key={regionName} className="space-y-3">
                  <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider border-b border-border pb-1">
                    {regionName}
                  </h3>
                  <div className="grid gap-3">{items.map((tpl) => renderTemplateCard(tpl))}</div>
                </div>
              ))}
            </div>
          );
        })()
      )}
    </div>
  );
}
